import {
  Autocomplete,
  autocompleteClasses,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
} from '@mui/material';
import { concat, filter, includes, isEmpty, join, map, size, some, sortBy, split, trim, uniq } from 'es-toolkit/compat';
import pluralize from 'pluralize';
import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { StringParam, useQueryParam } from 'use-query-params';
import validator from 'validator';

import { useTransformedDataTransfer } from 'components/SearchFilters/hooks/useTransformedDataTransfer';
import { AnnotationAssignment } from 'interfaces/annotation';
import { IFormValues } from './types';

interface Props {
  formMethods: UseFormReturn<Partial<IFormValues>, any>;
  annotationAssignments: AnnotationAssignment[];
  slidesInAnnotationAssignments: string[];
}

export const SelectSlidesFromList: React.FC<Props> = ({
  formMethods,
  annotationAssignments,
  slidesInAnnotationAssignments,
}) => {
  const {
    watch,
    control,
    formState: { errors },
  } = formMethods;

  const selectedAssignmentIds = watch('annotationAssignmentIds');
  const slideIds = watch('slideIds') || [];

  const slideIdsNotInAssignments = filter(slideIds, (id) => !includes(slidesInAnnotationAssignments, id));
  const hasSlideIdNotInAssignments = !isEmpty(slideIdsNotInAssignments);

  const [inputValue, setInputValue] = useQueryParam('slides', StringParam);
  const { handleOnDrop, handleOnPaste } = useTransformedDataTransfer(inputValue, setInputValue);

  return (
    <>
      <Grid item>
        <Controller
          control={control}
          name="slideIds"
          render={({ field: { onChange, value } }) => {
            const formError = errors['slideIds'];
            const hasInvalidSlideId = some(value, (id) => !validator.isUUID(id));
            const getOptionLabel = (slideId: string) => {
              const slideAssignments = sortBy(
                filter(
                  annotationAssignments,
                  (assignment) =>
                    includes(selectedAssignmentIds, assignment?.annotationAssignmentId) &&
                    some(assignment?.annotations, { slideId })
                )
              );
              return `${slideId}${
                isEmpty(slideAssignments)
                  ? ' (not in any assignment)'
                  : size(selectedAssignmentIds) > 1
                  ? ` (in ${pluralize('assignment', size(slideAssignments))} ${join(
                      map(slideAssignments, 'annotationAssignmentId'),
                      ', '
                    )})`
                  : ''
              }`;
            };
            return (
              <FormControl
                required
                fullWidth
                error={Boolean(formError || hasInvalidSlideId || hasSlideIdNotInAssignments)}
              >
                <Autocomplete<string, true, false, true>
                  disableCloseOnSelect
                  freeSolo
                  multiple
                  sx={{
                    mt: 2,
                    [`&.${autocompleteClasses.root} label`]: {
                      backgroundColor: 'background.paper',
                      px: 1,
                      left: -6,
                    },
                    [`&.${autocompleteClasses.root} .${autocompleteClasses.inputRoot}`]: {
                      maxHeight: 80,
                      overflowY: 'auto',
                      border: 1,
                    },
                    [`&.${autocompleteClasses.root} .${autocompleteClasses.inputRoot} fieldset`]: {
                      border: 'none',
                    },
                  }}
                  size="small"
                  disabled={isEmpty(selectedAssignmentIds)}
                  inputValue={inputValue || ''}
                  groupBy={(option) =>
                    includes(slidesInAnnotationAssignments, option)
                      ? 'In annotation assignment'
                      : 'Not in any annotation assignments'
                  }
                  getOptionLabel={getOptionLabel}
                  onInputChange={(event, newInputValue) => {
                    event?.preventDefault();
                    setInputValue(newInputValue);
                  }}
                  onDrop={handleOnDrop}
                  onPaste={handleOnPaste}
                  options={slidesInAnnotationAssignments}
                  value={value}
                  onChange={(_, newValue, changeType) => {
                    if (changeType === 'createOption') {
                      const splitInput = split(inputValue, ',');
                      const trimmedSplitInput = uniq(map(splitInput, (slide) => trim(slide)));
                      const newSlides = filter(trimmedSplitInput, (slideId) => validator.isUUID(slideId));
                      onChange(uniq(concat(value, newSlides)));
                    } else {
                      onChange(newValue);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Select slides for training" size="small" />}
                  renderTags={(tagValue, getTagProps) =>
                    map(tagValue, (slideId, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          {...tagProps}
                          key={key}
                          label={getOptionLabel(slideId)}
                          sx={
                            !validator.isUUID(slideId)
                              ? { backgroundColor: 'warning.main', color: 'white' }
                              : includes(slideIdsNotInAssignments, slideId)
                              ? { backgroundColor: 'error.main', color: 'white' }
                              : {}
                          }
                        />
                      );
                    })
                  }
                />
                <FormHelperText error={Boolean(errors['slideIds']) || hasInvalidSlideId || hasSlideIdNotInAssignments}>
                  {errors['slideIds']?.message
                    ? errors['slideIds']?.message
                    : hasInvalidSlideId
                    ? 'Some slide ids are invalid'
                    : hasSlideIdNotInAssignments
                    ? 'Some selected slides are not part of the selected annotation assignments'
                    : 'Select slides for training from the list or paste a list of slides'}
                </FormHelperText>
              </FormControl>
            );
          }}
        />
      </Grid>
      <Grid item>
        {hasSlideIdNotInAssignments && (
          <Controller
            control={control}
            name="slideIds"
            render={({ field: { onChange, value } }) => (
              <Button
                variant="text"
                color="secondary"
                size="small"
                onClick={(event) => {
                  event.preventDefault();
                  const onlySlidesInAssignments = filter(value, (id) => includes(slidesInAnnotationAssignments, id));
                  onChange(uniq(onlySlidesInAssignments));
                }}
              >
                Remove slides not in the selected annotation assignments
              </Button>
            )}
          />
        )}
      </Grid>
    </>
  );
};
